import { useCallback, useEffect, useRef, useState } from "react";
import { clockingApi } from "@/network/network.jsx";
import { mapAttendanceResponse, patchAttendanceIntoModel } from "../mappers/mapAttendanceResponse";
import type { AttendanceDetail, AttendanceListResponse, ClockingGridModel } from "../types";

/** Background refresh while Clocking is open (guide: optional poll). */
export const CLOCKING_ATTENDANCE_POLL_MS = 30_000;

export type AttendanceQuery = {
  startDate: string;
  endDate: string;
  exceptionsOnly?: boolean;
};

type RefreshOptions = {
  /** Quiet update — no loading flash; keep current grid on failure. */
  silent?: boolean;
};

export function useClockingAttendance(
  boardId: number | string | null | undefined,
  query: AttendanceQuery | null,
  enabled: boolean
) {
  const [model, setModel] = useState<ClockingGridModel | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const refresh = useCallback(
    async (opts?: RefreshOptions) => {
      const silent = opts?.silent ?? false;

      if (boardId == null || !query || !enabled) {
        if (!silent) {
          setModel(null);
        }
        return;
      }

      if (silent && inFlightRef.current) return;
      inFlightRef.current = true;

      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      try {
        const params: Record<string, string | number | boolean> = {
          start_date: query.startDate,
          end_date: query.endDate,
          exceptions_only: query.exceptionsOnly ?? true,
        };

        const { data } = await clockingApi(boardId).getAttendance(params);
        const mapped = mapAttendanceResponse(data as AttendanceListResponse);

        if (!mapped.groups.length && silent) {
          // Keep current grid through transient empty poll responses.
          return;
        }

        setModel(mapped);
        if (!silent) setError(null);
      } catch (err: unknown) {
        if (silent) {
          return;
        }
        setModel(null);
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          "Unable to load attendance.";
        setError(message);
      } finally {
        inFlightRef.current = false;
        if (!silent) setIsLoading(false);
      }
    },
    [boardId, query, enabled]
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!enabled || boardId == null || !query) return;

    const tick = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      void refresh({ silent: true });
    };

    const id = window.setInterval(tick, CLOCKING_ATTENDANCE_POLL_MS);

    const onVisibility = () => {
      if (!document.hidden) void refresh({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, boardId, query, refresh]);

  const applyAttendanceDetail = useCallback((detail: AttendanceDetail) => {
    setModel((prev) => (prev ? patchAttendanceIntoModel(prev, detail) : prev));
  }, []);

  return { model, isLoading, error, refresh, setModel, applyAttendanceDetail };
}
